/**
 * M2 HUD tuning (plan §6 M2-19): marker sizes, toast timing, every label and every toast line. The values live in
 * m2hud.json; the parsed object is mutable so the dev panel can bind it live (the model and view read it every
 * frame, except `maxContractMarkers`, `maxStationMarkers` and `toasts.max`, which size pools once at init).
 */
import { z } from 'zod'
import { CAREER_NOTICE } from '../../space/events'
import m2hudJson from './m2hud.json'

const positive = z.number().positive()
const nonNegative = z.number().nonnegative()
const label = z.string().min(1).max(64)
const line = z.string().min(1).max(200)

export const TOAST_KINDS = ['info', 'success', 'warning', 'error'] as const
export type ToastKind = (typeof TOAST_KINDS)[number]

/** Career notice names in `CAREER_NOTICE` order (the JSON keys). */
export type CareerNoticeName = keyof typeof CAREER_NOTICE
const careerNoticeNames = Object.keys(CAREER_NOTICE) as CareerNoticeName[]

const careerMessage = z.strictObject({
  kind: z.enum(TOAST_KINDS),
  /** `finance` = `speakers.finance`, `none` = no speaker line. */
  speaker: z.enum(['finance', 'none']),
  /** `{amount}` is replaced by the notice's credits. */
  text: line,
})

const careerMessages = z.strictObject(
  Object.fromEntries(careerNoticeNames.map((n) => [n, careerMessage])) as Record<CareerNoticeName, typeof careerMessage>,
)

export const m2HudTuningSchema = z.strictObject({
  /** Inset (CSS px) of off-screen markers from the viewport edge. */
  edgeMarginPx: nonNegative,
  /** M10 P5a12c: the placeholder ring at each marked rock (projected radius × scale, clamped, px). */
  boomRing: z.strictObject({ ringScale: positive, ringMinPx: nonNegative, ringMaxPx: positive }),
  /** Nav-target bracket half-size = radius × px/m + pad, clamped. */
  bracket: z.strictObject({ minHalfPx: positive, maxHalfPx: positive, padPx: nonNegative }),
  /** Salvage part brackets: a nominal part radius (m) and the clamp (CSS px). */
  partBracket: z.strictObject({ radiusM: positive, minHalfPx: positive, maxHalfPx: positive }),
  /** Contract markers in the pool (sized once at init). */
  maxContractMarkers: z.number().int().min(0).max(32),
  /**
   * Station markers in the pool (sized once at init, at least the stations then listed): M3 R22e switches the station
   * list with the Reach system, so the pool covers the most stations one system can have.
   */
  maxStationMarkers: z.number().int().min(0).max(32),
  /** The lease countdown turns to its warning state below this many game seconds. */
  leaseWarnSeconds: nonNegative,
  toasts: z.strictObject({
    /** Toast slots (sized once at init); a new toast replaces the oldest when all are up. */
    max: z.number().int().min(1).max(8),
    /** Seconds a toast stays up (real time, paused time excluded). */
    seconds: positive,
    /** The last this-many seconds fade out. */
    fadeSeconds: nonNegative,
  }),
  /**
   * CL-8 (audit confusing 7): a station marker beyond its mass-lock range (reach-travel.json `massLock.stationM` off
   * the hull) and its cruise auto-drop adds a cruise time after the distance, "40.9 Gm · ~12 s cruise". Whole seconds
   * below `minutesFromS`, then whole minutes; `{n}` is the number.
   */
  cruiseEta: z.strictObject({ minutesFromS: positive, separator: z.string().max(8), seconds: label, minutes: label }),
  labels: z.strictObject({
    /** Mode badge per `TOOL` mode (combat, mining, salvage). */
    modes: z.tuple([label, label, label]),
    chip: label,
    fracture: label,
    tooHard: label,
    overheated: label,
    tooLarge: label,
    holdFull: label,
    outOfRange: label,
    tractorOn: label,
    autodock: label,
    /** CL-2: the next step after clearance within `docking.autodockRange` of the hull; `{key}` = the dock binding's glyph. */
    dockPrompt: label,
    /** CL-2: the same beyond the range; `{range}` = `docking.autodockRange` as a HUD distance ("2.0 km"). */
    dockPromptFar: label,
    /** CL-2: the dock key's glyph while the pad is the active device (D-pad ↓ held `dockHoldSeconds`, bindings.json). */
    dockPadGlyph: label,
    cleared: label,
    docked: label,
    collectionsNotice: label,
    collectionsRepo: label,
    owned: label,
    scanning: label,
    noScan: label,
    coolant: label,
    /** Wreck scan condition band per `CONDITION_BAND` (intact, serviceable, wrecked). */
    bands: z.tuple([label, label, label]),
    /** Wreck scan row note when a part is heavier than the fitted racks take; `{module}` is the smallest rack that fits. */
    needsRacks: label,
    /** Wreck scan row Mk of an Intact Mk-carrying part (Mk I-III; economy plan SY-4b), followed by `installable`. */
    partMk: z.tuple([label, label, label]),
    installable: label,
    /** Wreck scan row note of a Rush-pattern part (M8 MC-h1 tags it, the part pool's `rush` column; MC-a6 draws it). */
    rushPattern: label,
    /** Rock scan field line (M8 MC-a6, every Mk): `{grade}` is one of `grades` (galaxy.json minerals.grades order). */
    grade: label,
    grades: z.tuple([label, label, label, label]),
    /** Rock scan field line: the system's signature ore when the rock's field holds it; `{ore}` is the ore name. */
    signature: label,
    unknownValue: label,
    contractTypes: z.strictObject({ delivery: label, recovery: label, clearRaiders: label, courier: label }),
  }),
  speakers: z.strictObject({
    /** The lease holder's comms voice. */
    finance: label,
    /** A station controller is "<station name><stationSuffix>". */
    stationSuffix: z.string().max(32),
  }),
  messages: z.strictObject({
    career: careerMessages,
    /** `{seconds}` = clearance window. */
    clearanceGranted: line,
    /** `tooLarge` (M10 P1e1): the bay doesn't take the ship's size band. */
    clearanceDenied: z.strictObject({ none: line, tooFar: line, notReady: line, hostileLock: line, tooLarge: line }),
    clearanceExpired: line,
    /** CL-2: the `autodockStart` toast. */
    autodockEngaged: line,
    /** CL-2: the `autodockCancel` toast by cause: flight input, the clearance ran out, anything else. */
    autodockCancelled: z.strictObject({ stick: line, expired: line, other: line }),
    /** M10 P1e2: a hull too big for the bay is granted a moor (the `moorGranted` space event). */
    moorGranted: line,
    /** M10 P1e2: the moor hold completed (the `moored` space event); moored counts as docked. */
    moored: line,
    /** `{amount}` = hull damage. */
    impact: line,
    holdFull: z.strictObject({ chunk: line, part: line }),
    groupDefeated: z.strictObject({ raiders: line, repo: line, contract: line }),
  }),
})

export type M2HudTuning = z.infer<typeof m2HudTuningSchema>

export function parseM2HudTuning(raw: unknown): M2HudTuning {
  const t = m2HudTuningSchema.parse(raw)
  if (t.bracket.minHalfPx > t.bracket.maxHalfPx) throw new Error('m2hud: bracket.minHalfPx exceeds maxHalfPx')
  if (t.partBracket.minHalfPx > t.partBracket.maxHalfPx) throw new Error('m2hud: partBracket.minHalfPx exceeds maxHalfPx')
  return t
}

/** The live tuning, parsed once from m2hud.json. */
export const m2HudTuning: M2HudTuning = parseM2HudTuning(m2hudJson)
